import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress,
  Chip,
  Divider,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  School,
  CalendarToday,
  Phone,
  Email,
  Restore,
  History,
  Assessment,
  Interests,
  SportsEsports,
  Work,
} from "@mui/icons-material";
import axios from "axios";

const TransferredLearnerDetails = () => {
  const { learnerId } = useParams();
  const navigate = useNavigate();
  
  const [learner, setLearner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [readmitDialogOpen, setReadmitDialogOpen] = useState(false);
  const [readmissionData, setReadmissionData] = useState({
    readmissionClass: "",
    readmissionStream: "",
    remarks: "",
  });
  const [readmitting, setReadmitting] = useState(false);

  useEffect(() => {
    fetchLearnerDetails();
  }, [learnerId]);

  const fetchLearnerDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://ed-9ny1.onrender.com/api/transfers/details/${learnerId}`
      );
      
      if (response.data.success) {
        setLearner(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching learner details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReadmit = async () => {
    try {
      setReadmitting(true);
      const response = await axios.post(
        `https://ed-9ny1.onrender.com/api/transfers/readmit/${learnerId}`,
        {
          readmittedBy: "user_id_here", // Replace with actual user ID
          ...readmissionData,
        }
      );

      if (response.data.success) {
        alert("Learner readmitted successfully!");
        navigate("/all-students");
      }
    } catch (error) {
      console.error("Error readmitting learner:", error);
      alert("Failed to readmit learner");
    } finally {
      setReadmitting(false);
      setReadmitDialogOpen(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const calculateAge = (dateOfBirth) => {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!learner) {
    return (
      <Container sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Learner not found
        </Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Avatar sx={{ width: 60, height: 60, mr: 2, bgcolor: "#6200ee" }}>
              {learner.fullName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {learner.fullName}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {learner.admissionNumber} • {calculateAge(learner.dateOfBirth)} years old
              </Typography>
            </Box>
            <Box sx={{ ml: "auto" }}>
              <Chip
                label="Transferred"
                color="secondary"
                sx={{ mr: 1 }}
              />
              {!learner.readmissionDetails?.readmitted && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Restore />}
                  onClick={() => setReadmitDialogOpen(true)}
                >
                  Readmit
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab icon={<Person />} label="Personal Info" />
            <Tab icon={<History />} label="Transfer Details" />
            <Tab icon={<Assessment />} label="Academic History" />
            <Tab icon={<Interests />} label="Interests" />
            <Tab icon={<SportsEsports />} label="Game Performance" />
            <Tab icon={<Work />} label="Career Insights" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                    Personal Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Birth Certificate
                      </Typography>
                      <Typography variant="body1">
                        {learner.birthCertificateNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Gender
                      </Typography>
                      <Typography variant="body1">
                        {learner.gender === "M" ? "Male" : "Female"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Date of Birth
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(learner.dateOfBirth)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Age
                      </Typography>
                      <Typography variant="body1">
                        {calculateAge(learner.dateOfBirth)} years
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                    Academic Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Class
                      </Typography>
                      <Typography variant="body1">
                        {learner.class}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Stream
                      </Typography>
                      <Typography variant="body1">
                        {learner.stream || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Admission Number
                      </Typography>
                      <Typography variant="body1">
                        {learner.admissionNumber}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                    Parent Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Person sx={{ mr: 1, color: "primary.main" }} />
                        <Typography variant="body1">
                          {learner.parentName}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Phone sx={{ mr: 1, color: "primary.main" }} />
                        <Typography variant="body1">
                          {learner.parentContact}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Email sx={{ mr: 1, color: "primary.main" }} />
                        <Typography variant="body1">
                          {learner.parentEmail}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Transfer Details
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom color="primary">
                        Transfer Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary">
                          Transfer Date
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(learner.transferDetails.transferDate)}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary">
                          Reason
                        </Typography>
                        <Typography variant="body1">
                          {learner.transferDetails.transferReason.replace(/_/g, " ")}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary">
                          Destination School
                        </Typography>
                        <Typography variant="body1">
                          {learner.transferDetails.destinationSchool || "Not specified"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Transferred By
                        </Typography>
                        <Typography variant="body1">
                          {learner.transferDetails.transferredBy?.fullName || "Unknown"}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom color="primary">
                        Remarks
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {learner.transferDetails.remarks || "No remarks provided"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {learner.readmissionDetails?.readmitted && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderColor: "success.main" }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom color="success.main">
                          Readmission Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="textSecondary">
                              Readmission Date
                            </Typography>
                            <Typography variant="body1">
                              {formatDate(learner.readmissionDetails.readmissionDate)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="textSecondary">
                              Readmission Class
                            </Typography>
                            <Typography variant="body1">
                              {learner.readmissionDetails.readmissionClass}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="textSecondary">
                              Readmission Stream
                            </Typography>
                            <Typography variant="body1">
                              {learner.readmissionDetails.readmissionStream || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">
                              Readmission Remarks
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                              {learner.readmissionDetails.remarks || "No remarks"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Academic History
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              {/* Academic History Table */}
              {learner.academicHistory && learner.academicHistory.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Academic Year</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Stream</TableCell>
                        <TableCell>Final Grade</TableCell>
                        <TableCell>Remarks</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {learner.academicHistory.map((history, index) => (
                        <TableRow key={index}>
                          <TableCell>{history.academicYear}</TableCell>
                          <TableCell>{history.class}</TableCell>
                          <TableCell>{history.stream || "N/A"}</TableCell>
                          <TableCell>{history.finalGrade || "N/A"}</TableCell>
                          <TableCell>{history.remarks || "N/A"}</TableCell>
                          <TableCell>{formatDate(history.promotionDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
                  No academic history available
                </Typography>
              )}

              {/* Results Table */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                  Results
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {learner.results && learner.results.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Term</TableCell>
                          <TableCell>Exam Type</TableCell>
                          <TableCell>Subject</TableCell>
                          <TableCell>Marks</TableCell>
                          <TableCell>Academic Year</TableCell>
                          <TableCell>Class</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {learner.results.slice(0, 10).map((result, index) => (
                          <TableRow key={index}>
                            <TableCell>{result.term}</TableCell>
                            <TableCell>{result.examType}</TableCell>
                            <TableCell>{result.subject}</TableCell>
                            <TableCell>{result.marks}</TableCell>
                            <TableCell>{result.academicYear}</TableCell>
                            <TableCell>{result.className}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
                    No results available
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Add similar content for other tabs (interests, game performance, career insights) */}
        {/* This would follow the same pattern as above tabs */}

        {/* Readmission Dialog */}
        <Dialog
          open={readmitDialogOpen}
          onClose={() => !readmitting && setReadmitDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Readmit Learner</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are about to readmit <strong>{learner.fullName}</strong> back to active learners.
            </Alert>

            <TextField
              fullWidth
              label="Class for Readmission"
              value={readmissionData.readmissionClass}
              onChange={(e) => setReadmissionData({ ...readmissionData, readmissionClass: e.target.value })}
              margin="normal"
              required
              defaultValue={learner.class}
            />

            <TextField
              fullWidth
              label="Stream (Optional)"
              value={readmissionData.readmissionStream}
              onChange={(e) => setReadmissionData({ ...readmissionData, readmissionStream: e.target.value })}
              margin="normal"
              defaultValue={learner.stream}
            />

            <TextField
              fullWidth
              label="Remarks"
              value={readmissionData.remarks}
              onChange={(e) => setReadmissionData({ ...readmissionData, remarks: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              placeholder="Add any remarks about this readmission..."
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setReadmitDialogOpen(false)}
              disabled={readmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReadmit}
              variant="contained"
              color="primary"
              disabled={readmitting}
            >
              {readmitting ? (
                <CircularProgress size={24} />
              ) : (
                "Readmit Learner"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Container>
  );
};

export default TransferredLearnerDetails;